import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import clsx from "clsx";
import { OnboardingGlow } from "../../components/backgrounds/OnboardingGlow";

// ─── Sentence structure ──────────────────────────────────────────────────────
//
// Each part maps to a key on the getBusinessExamples response. `connector` is
// the copy that precedes the part's slot — spacing and punctuation included —
// so the sentence reads:
//   Fluvo can <outcome>, by following up with <audience>, and immediately
//   halting all nudges once <action>.

const SENTENCE_LEAD = "Fluvo can ";
const SENTENCE_TAIL = ".";

const SENTENCE_PARTS = Object.freeze([
  {
    id: "outcome",
    connector: "",
    prompt: "What do you want to achieve?",
    placeholder: "the outcome",
  },
  {
    id: "audience",
    connector: ", by following up with ",
    prompt: "Who should these journeys reach?",
    placeholder: "the audience",
  },
  {
    id: "action",
    connector: ", and immediately halting all nudges once ",
    prompt: "What should they do?",
    placeholder: "the action",
  },
]);

const OPTION_KEYS = ["A", "B", "C", "D", "E", "F", "G", "H"];

// The API returns { full_text, display_text }, camel-cased by the network layer.
// Long text goes in the option row, short text into the sentence.
const fullText = (option) => option.fullText ?? option.full_text ?? "";
const displayText = (option) =>
  option.displayText ?? option.display_text ?? fullText(option);

// ─── Sentence slot ───────────────────────────────────────────────────────────

function Slot({ part, answer, state }) {
  return (
    <span
      className={clsx(
        "inline-block rounded-lg px-2.5 py-0.5 align-baseline font-semibold",
        state === "answered" &&
          "border border-primary/30 bg-primary/10 text-primary-dark",
        // Blanks are deliberately wider than their placeholder, so centre the
        // text rather than leaving a gap before the following punctuation.
        // Only the border marks the active blank — its text stays grey, so an
        // empty slot never reads as though it has already been answered.
        state === "active" &&
          "min-w-[160px] border border-dashed border-primary bg-white text-center font-medium text-gray-400",
        state === "pending" &&
          "min-w-[140px] border border-dashed border-gray-300 bg-white text-center font-medium text-gray-300",
      )}
    >
      {state === "answered" ? displayText(answer) : part.placeholder}
    </span>
  );
}

// ─── Loading placeholder ─────────────────────────────────────────────────────

const OptionsSkeleton = () => (
  <div className="space-y-2.5">
    {[0, 1, 2].map((i) => (
      <div key={i} className="h-14 w-full rounded-xl bg-gray-100 shimmer" />
    ))}
  </div>
);

// ─── Main component ──────────────────────────────────────────────────────────

// `active` is false while the step is mounted but hidden (the user stepped
// back or forward) — the keyboard shortcuts must not answer for them then.
export default function GoalStep({ examples, value, onChange, active = true }) {
  const [answers, setAnswers] = useState(() => value || {});

  // Only parts the API actually returned options for are askable.
  const parts = useMemo(
    () => SENTENCE_PARTS.filter((part) => examples?.[part.id]?.length),
    [examples],
  );

  // The first unanswered part — the only one that can be acted on.
  const currentIndex = useMemo(() => {
    const index = parts.findIndex((part) => !answers[part.id]);
    return index === -1 ? parts.length : index;
  }, [answers, parts]);

  const currentPart = parts[currentIndex];
  const options = currentPart ? examples[currentPart.id] : [];
  const [highlight, setHighlight] = useState(0);

  // Reset the keyboard cursor whenever the question changes.
  useEffect(() => {
    setHighlight(0);
  }, [currentIndex]);

  const commit = useCallback(
    (option) => {
      if (!currentPart) return;
      const updated = { ...answers, [currentPart.id]: option };
      setAnswers(updated);
      onChange && onChange(updated);
    },
    [answers, currentPart, onChange],
  );

  // Unpicks the most recent answer, whether or not a question is on screen.
  const goBack = useCallback(() => {
    const previous = parts[currentIndex - 1];
    if (!previous) return;
    const updated = { ...answers };
    delete updated[previous.id];
    setAnswers(updated);
    onChange && onChange(updated);
  }, [answers, currentIndex, onChange, parts]);

  // ↑↓ to move · A–C to jump · Enter to lock in · ← / Backspace to go back
  useEffect(() => {
    if (!active) return;

    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft" || event.key === "Backspace") {
        event.preventDefault();
        goBack();
        return;
      }
      if (!currentPart) return;

      const optionCount = options.length;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlight((prev) => (prev + 1) % optionCount);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlight((prev) => (prev - 1 + optionCount) % optionCount);
      } else if (event.key === "Enter") {
        event.preventDefault();
        commit(options[highlight]);
      } else {
        const keyIndex = OPTION_KEYS.indexOf(event.key.toUpperCase());
        if (keyIndex >= 0 && keyIndex < optionCount) {
          event.preventDefault();
          commit(options[keyIndex]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [active, commit, currentPart, goBack, highlight, options]);

  const loading = !parts.length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      // The header's top margin is padding on the root instead, so it can't
      // collapse out and push the glow down away from where step 1 puts it.
      className="relative pt-4"
    >
      <OnboardingGlow />

      {/* Header — matches the framing of step 1 */}
      <header className="bg-card relative w-full rounded-xl max-w-7xl mx-auto mb-4 top-0 left-0 right-0 z-50 px-6 py-6">
        <div className="flex flex-col items-center justify-center text-center space-y-3 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-gray-200 text-xs font-medium text-gray-600">
            <span>🎯</span> Let's start with one simple goal you would like to
            achieve, that Fluvo can automate.
          </div>
          {/* The sentence is the heading — it assembles as answers lock in */}
          <h1 className="text-4xl leading-[1.7] font-normal text-gray-900">
            {SENTENCE_LEAD}
            {SENTENCE_PARTS.map((part, index) => {
              const last = index === SENTENCE_PARTS.length - 1;
              const askableIndex = parts.indexOf(part);
              return (
                <span key={part.id}>
                  {part.connector}
                  <Slot
                    part={part}
                    answer={answers[part.id]}
                    state={
                      answers[part.id]
                        ? "answered"
                        : askableIndex === currentIndex
                          ? "active"
                          : "pending"
                    }
                  />
                  {last && SENTENCE_TAIL}
                </span>
              );
            })}
          </h1>
          <p className="text-base text-gray-600 leading-relaxed">
            Answer three quick questions to finish the sentence — we'll build
            your journeys against exactly what it says. Change any answer before
            you continue.
          </p>
        </div>
      </header>

      <div
        className="relative max-w-3xl mx-auto pt-2"
        style={{ paddingBottom: "200px" }}
      >
        {/* One question at a time — nothing else is answerable */}
        <div className="mt-8">
          {loading && <OptionsSkeleton />}

          {!loading && currentPart && (
            <motion.div
              key={currentPart.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
            >
              <div className="flex items-baseline gap-3">
                <span className="text-[11px] font-medium text-primary uppercase tracking-wider">
                  Question {currentIndex + 1} of {parts.length}
                </span>
                <span className="text-sm text-gray-600">
                  {currentPart.prompt}
                </span>
              </div>

              <div className="mt-3 flex flex-col gap-2.5">
                {options.map((option, index) => {
                  const focused = index === highlight;
                  return (
                    <button
                      key={fullText(option)}
                      type="button"
                      onMouseEnter={() => setHighlight(index)}
                      onClick={() => commit(option)}
                      className={clsx(
                        "flex cursor-pointer items-center gap-3.5 rounded-xl border px-4 py-3.5 text-left text-sm leading-normal text-gray-900",
                        focused
                          ? "border-primary bg-primary/5 ring-4 ring-primary/10"
                          : "border-gray-200 bg-white hover:border-gray-300",
                      )}
                    >
                      <span
                        className={clsx(
                          "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border text-[11px] font-medium",
                          focused
                            ? "border-primary text-primary"
                            : "border-gray-300 text-gray-400",
                        )}
                      >
                        {OPTION_KEYS[index]}
                      </span>
                      {fullText(option)}
                    </button>
                  );
                })}
              </div>

              <div className="mt-3.5 flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  ↑↓ to move · Enter to lock in
                </span>
                {currentIndex > 0 && (
                  <button
                    type="button"
                    onClick={goBack}
                    className="cursor-pointer text-xs text-gray-400 underline hover:text-gray-600"
                  >
                    ← change my last answer
                  </button>
                )}
              </div>
            </motion.div>
          )}

          {!loading && !currentPart && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="flex flex-col items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-6 py-5 text-center"
            >
              <p className="text-sm font-medium text-primary-dark">
                Your sentence is complete.
              </p>
              <button
                type="button"
                onClick={goBack}
                className="cursor-pointer text-xs text-gray-500 underline hover:text-gray-700"
              >
                ← change my last answer
              </button>
            </motion.div>
          )}
        </div>

        {/* Progress */}
        {!loading && (
          <div className="mt-8 flex items-center justify-center gap-1.5">
            {parts.map((part, index) => (
              <div
                key={part.id}
                className={clsx(
                  "h-[3px] w-9 rounded-sm",
                  answers[part.id]
                    ? "bg-primary"
                    : index === currentIndex
                      ? "bg-primary/40"
                      : "bg-gray-200",
                )}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export { SENTENCE_PARTS };
